import { Component, type ErrorInfo, type ReactNode } from 'react'
import { getLastTraceId } from '@/lib/trace'
import { Button } from './ui/Button'
import { Alert } from './ui/misc'

/* Two boundaries exist in this application:

     - one at the route level, which may unmount and re-render its subtree;
     - one wrapping the exam, which must NEVER unmount the exam store, so a
       render crash in a single question cannot destroy answers.

   The difference is entirely in what the fallback does, not in this class. */

interface Props {
  children: ReactNode
  /** Rendered instead of the subtree. Receives a reset that clears the error. */
  fallback?: (error: Error, reset: () => void, traceId: string | null) => ReactNode
  /** Reported alongside the error so a log search knows where it happened. */
  where: string
  onError?: (error: Error, info: ErrorInfo) => void
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info)
    reportCrash(error, this.props.where, info.componentStack ?? null)
  }

  private reset = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (error === null) return this.props.children

    if (this.props.fallback !== undefined) {
      return this.props.fallback(error, this.reset, getLastTraceId())
    }

    return (
      <div style={{ padding: 'var(--s-6)', maxWidth: '52ch', margin: '0 auto' }}>
        <Alert tone="danger" title="This page could not be displayed">
          <p>
            Something went wrong while rendering. Your data has not been lost — reloading usually
            fixes it.
          </p>
          {getLastTraceId() !== null && (
            <p style={{ fontSize: 'var(--fs-xs)' }}>
              Reference: <code>{getLastTraceId()}</code>
            </p>
          )}
          <div style={{ display: 'flex', gap: 'var(--s-3)', marginTop: 'var(--s-3)' }}>
            <Button onClick={this.reset}>Try again</Button>
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Reload the page
            </Button>
          </div>
        </Alert>
      </div>
    )
  }
}

/* Unhandled render crashes go to the same sink as failed requests, carrying the
   trace id of the most recent request — which is usually what caused it. */
function reportCrash(error: Error, where: string, componentStack: string | null): void {
  const base = import.meta.env.VITE_API_BASE_URL ?? '/api'
  const payload = {
    trace_id: getLastTraceId(),
    endpoint: null,
    status: null,
    code: 'RENDER_CRASH',
    message: `${where}: ${error.message}`,
    stack: [error.stack, componentStack].filter(Boolean).join('\n\n'),
    build_id: import.meta.env.VITE_BUILD_ID ?? 'local',
    route: window.location.pathname,
    user_agent: navigator.userAgent,
    occurred_at: new Date().toISOString(),
  }
  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
    navigator.sendBeacon?.(`${base}/v1/telemetry/errors`, blob)
  } catch {
    /* best effort */
  }
}

/* Unhandled errors and promise rejections outside React's tree. Installed once
   from main.tsx. */
export function installGlobalErrorReporting(): void {
  window.addEventListener('error', (event) => {
    reportCrash(event.error instanceof Error ? event.error : new Error(event.message), 'window', null)
  })
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    reportCrash(reason instanceof Error ? reason : new Error(String(reason)), 'promise', null)
  })
}
